import PageLayout from "@/components/PageLayout";
import { FlowDiagram } from "@/components/FlowDiagram";

/**
 * /cadre-juridique — Reference page presenting the legal framework
 * surrounding digital exposure of individuals tied to professional or
 * asset-related activities. Replaces the former /droits-rgpd page with
 * a broader, more institutional scope.
 */

const RGPD_RIGHTS = [
  {
    reference: "Article 15 du RGPD",
    title: "Droit d'accès",
    desc: "Comprendre quelles données personnelles sont traitées, d'où elles proviennent, à quelles fins elles sont utilisées et sur quelle base juridique elles reposent.",
  },
  {
    reference: "Article 16 du RGPD",
    title: "Droit de rectification",
    desc: "Corriger des données inexactes, incomplètes ou devenues obsolètes.",
  },
  {
    reference: "Article 21 du RGPD",
    title: "Droit d'opposition",
    desc: "Contester certains traitements lorsque la situation particulière de la personne concernée le justifie.",
  },
  {
    reference: "Article 18 du RGPD",
    title: "Droit à la limitation",
    desc: "Demander le gel temporaire de certains traitements pendant l'examen d'une contestation.",
  },
  {
    reference: "Article 17 du RGPD",
    title: "Droit à l'effacement",
    desc: "Demander la suppression de certaines données lorsque les conditions légales sont réunies.",
  },
  {
    reference: "Article 17 du RGPD et jurisprudence de la CJUE",
    title: "Désindexation",
    desc: "Réduire la visibilité de certaines pages dans les moteurs de recherche. Le déréférencement n'est pas automatique : il dépend d'une mise en balance entre les droits de la personne, la liberté d'information et l'intérêt du public.",
  },
];

const VIE_PRIVEE = [
  {
    title: "Article 9 du Code civil",
    desc: "Le droit français protège le respect de la vie privée. Dans certaines situations, la diffusion ou la rediffusion d'informations peut soulever une question de proportionnalité entre transparence et protection des personnes.",
  },
  {
    title: "Atteinte disproportionnée",
    desc: "L'enjeu n'est pas toujours l'existence initiale d'une donnée mais l'effet produit par son agrégation, sa durée de visibilité ou son indexation massive.",
  },
  {
    title: "Mesures judiciaires",
    desc: "Dans certaines situations particulières, des mesures civiles peuvent être envisagées afin de faire cesser une atteinte ou limiter ses conséquences.",
  },
];

const CNIL = [
  {
    title: "Réclamation CNIL",
    desc: "Lorsqu'une personne estime que ses droits n'ont pas été respectés, elle peut saisir la CNIL après avoir effectué les démarches préalables nécessaires.",
  },
  {
    title: "Documentation des situations",
    desc: "La constitution d'un dossier précis demeure souvent une étape essentielle : sources, URL, captures, dates, réponses reçues et historique des échanges.",
  },
  {
    title: "Dialogue avec les responsables de traitement",
    desc: "De nombreuses situations peuvent être examinées directement avec les plateformes, éditeurs ou responsables de traitement concernés.",
  },
];

const DISTINCTIONS = [
  {
    title: "Les faits",
    desc: "Ce qui est observable et documentable : la page, l'URL, le document, la date, le contenu effectivement accessible.",
  },
  {
    title: "Les principes juridiques",
    desc: "Les règles applicables au traitement : finalité, base juridique, proportionnalité, durée de conservation, droits des personnes.",
  },
  {
    title: "Les interprétations",
    desc: "La lecture de ces règles dans une situation donnée, qui relève d'une analyse et d'une mise en balance pouvant être discutées.",
  },
  {
    title: "Les demandes pouvant être formulées",
    desc: "Retrait, masquage, désindexation, limitation, opposition, information, formulées de manière ciblée et motivée.",
  },
  {
    title: "Les résultats",
    desc: "Ils restent dépendants de chaque situation, des réponses des acteurs concernés et, le cas échéant, des autorités saisies.",
  },
];

function SectionEyebrow({ n, label }) {
  return (
    <p className="label-eyebrow mb-6">
      <span className="text-[#B83A34]">{String(n).padStart(2, "0")}</span>
      {" / "}
      {label}
    </p>
  );
}

function CardGrid({ items, cols = 3, testid }) {
  const cls =
    cols === 2
      ? "grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-12"
      : "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12";
  return (
    <ul data-testid={testid} className={cls}>
      {items.map((it, i) => (
        <li
          key={it.title}
          data-testid={`${testid}-${i}`}
          className="border-l border-[#D8D3CD] pl-6 py-1 flex flex-col"
        >
          <span className="font-mono text-[11px] tracking-[0.14em] uppercase text-[#B83A34] mb-4">
            {it.reference ? it.reference : `${String(i + 1).padStart(2, "0")} / ${String(items.length).padStart(2, "0")}`}
          </span>
          <h3 className="font-serif text-xl lg:text-[22px] text-[#181A1D] font-normal leading-snug tracking-tight mb-3 text-balance">
            {it.title}
          </h3>
          <p className="text-sm text-[#666A70] font-normal leading-relaxed text-pretty">
            {it.desc}
          </p>
        </li>
      ))}
    </ul>
  );
}

export default function CadreJuridique() {
  return (
    <PageLayout
      eyebrow="Cadre juridique"
      title={<>Les droits qui permettent d'<em className="italic font-normal">agir</em> sur vos informations en ligne.</>}
      pattern="documentary"
      intro="L'exposition d'un dirigeant, d'un associé, d'un indépendant ou d'une structure patrimoniale ne se réduit pas à un texte unique. RGPD, obligations de publicité légale, protection de la vie privée, démarches CNIL, désindexation, recours plus sensibles : ces leviers se combinent et se hiérarchisent selon la source, la nature de la donnée et le contexte."
    >
      {/* Section 01 — Équilibre */}
      <section
        data-testid="cadre-section-01"
        className="bg-[#FAF9F7] border-b border-[#E2DEDA]"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <SectionEyebrow n={1} label="Principe" />
            <h2 className="font-serif font-normal text-[#181A1D] text-3xl lg:text-4xl xl:text-5xl tracking-tight leading-[1.06] text-balance">
              Un équilibre entre transparence et vie privée.
            </h2>
            <div className="h-[2px] w-10 bg-[#E94B3C] mt-10" />
          </div>
          <div className="lg:col-span-7 space-y-6 text-[15px] lg:text-base text-[#52565C] font-normal leading-relaxed">
            <p>
              La visibilité de certaines informations professionnelles ou
              patrimoniales résulte parfois d'obligations légales de
              publication. Cependant, la rediffusion, l'agrégation,
              l'indexation et la conservation de ces données peuvent
              soulever des questions nouvelles lorsque leur exposition
              devient durable, massive ou difficilement maîtrisable.
            </p>
            <p>
              L'objectif n'est pas d'effacer l'existence des registres
              officiels mais d'examiner comment certaines données sont
              reprises, diffusées et utilisées au-delà de leur finalité
              initiale.
            </p>
          </div>
        </div>
      </section>

      {/* Section 02 — Droits RGPD */}
      <section data-testid="cadre-section-02" className="bg-[#FAF9F7] border-b border-[#E2DEDA]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
            <div className="lg:col-span-5">
              <SectionEyebrow n={2} label="RGPD" />
              <h2 className="font-serif font-normal text-[#181A1D] text-3xl lg:text-4xl xl:text-5xl tracking-tight leading-[1.06] text-balance">
                Les droits issus du RGPD.
              </h2>
            </div>
            <div className="lg:col-span-7 flex items-end">
              <p className="text-[15px] lg:text-base text-[#52565C] font-normal leading-relaxed max-w-2xl">
                Ces droits ne produisent pas tous les mêmes effets et ne sont
                pas applicables de manière identique selon la nature de la
                source, les obligations légales existantes et le contexte de
                diffusion.
              </p>
            </div>
          </div>
          <CardGrid items={RGPD_RIGHTS} cols={3} testid="cadre-rgpd-grid" />

          <aside
            data-testid="cadre-principes-rediffusion"
            className="mt-10 border border-[#E2DEDA] bg-white rounded-md p-7 lg:p-8 max-w-4xl"
          >
            <p className="label-eyebrow text-[#666A70] mb-3">
              Les principes qui encadrent la rediffusion
            </p>
            <div className="space-y-4 text-[13px] lg:text-sm text-[#5A5E64] font-normal leading-relaxed text-pretty">
              <p>
                Au-delà des droits individuels, toute rediffusion de
                données obéit à des principes généraux. L'article 5 du
                RGPD impose la finalité, la minimisation, l'exactitude et
                la limitation de la conservation. L'article 6 exige une
                base de licéité pour chaque traitement : souvent
                l'intérêt légitime, qui suppose une mise en balance avec
                les droits des personnes. Les articles 12 à 14 organisent
                la transparence et l'information des personnes, y compris
                lorsque les données proviennent de sources tierces. En
                droit français, l'article 9 du Code civil protège par
                ailleurs le droit au respect de la vie privée.
              </p>
              <p>
                Textes de référence :{" "}
                <a
                  href="https://eur-lex.europa.eu/legal-content/FR/TXT/?uri=CELEX:32016R0679"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="cadre-lien-rgpd"
                  className="underline decoration-[#D8D3CD] underline-offset-4 text-[#181A1D] hover:decoration-[#181A1D] transition-colors"
                >
                  texte intégral du RGPD
                </a>{" "}
                ·{" "}
                <a
                  href="https://www.legifrance.gouv.fr/codes/article_lc/LEGIARTI000006419288"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="cadre-lien-code-civil"
                  className="underline decoration-[#D8D3CD] underline-offset-4 text-[#181A1D] hover:decoration-[#181A1D] transition-colors"
                >
                  article 9 du Code civil
                </a>
                .
              </p>
            </div>
          </aside>
        </div>
      </section>

      {/* Section 03 — Vie privée */}
      <section data-testid="cadre-section-03" className="bg-[#F2F0ED] border-b border-[#E2DEDA]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
            <div className="lg:col-span-5">
              <SectionEyebrow n={3} label="Vie privée" />
              <h2 className="font-serif font-normal text-[#181A1D] text-3xl lg:text-4xl xl:text-5xl tracking-tight leading-[1.06] text-balance">
                Protection de la vie privée.
              </h2>
            </div>
            <div className="lg:col-span-7 flex items-end">
              <p className="text-[15px] lg:text-base text-[#52565C] font-normal leading-relaxed max-w-2xl">
                Au-delà du RGPD, le droit civil français protège la vie privée
                des personnes. Cette protection peut être mobilisée
                lorsqu'une rediffusion devient disproportionnée au regard de
                sa finalité initiale.
              </p>
            </div>
          </div>
          <CardGrid items={VIE_PRIVEE} cols={3} testid="cadre-vie-privee-grid" />
        </div>
      </section>

      {/* Section 04 — CNIL */}
      <section data-testid="cadre-section-04" className="bg-[#FAF9F7] border-b border-[#E2DEDA]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
            <div className="lg:col-span-5">
              <SectionEyebrow n={4} label="Autorités" />
              <h2 className="font-serif font-normal text-[#181A1D] text-3xl lg:text-4xl xl:text-5xl tracking-tight leading-[1.06] text-balance">
                CNIL et autorités compétentes.
              </h2>
            </div>
            <div className="lg:col-span-7 flex items-end">
              <p className="text-[15px] lg:text-base text-[#52565C] font-normal leading-relaxed max-w-2xl">
                Le dialogue avec les responsables de traitement, la
                documentation rigoureuse des situations et le recours aux
                autorités compétentes constituent des étapes complémentaires
                à examiner selon les cas.
              </p>
            </div>
          </div>
          <CardGrid items={CNIL} cols={3} testid="cadre-cnil-grid" />
        </div>
      </section>

      {/* Section 05 — Publicité légale & rediffusions privées (FLOW) */}
      <section
        data-testid="cadre-section-05"
        className="bg-[#F2F0ED] border-b border-[#E2DEDA]"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <SectionEyebrow n={5} label="Sujet central" />
              <h2 className="font-serif font-normal text-[#181A1D] text-3xl lg:text-4xl xl:text-5xl tracking-tight leading-[1.06] text-balance">
                Publicité légale et rediffusions privées.
              </h2>
            </div>
            <div className="lg:col-span-7 space-y-5 text-[15px] lg:text-base text-[#52565C] font-normal leading-relaxed">
              <p>
                La publicité légale poursuit un objectif de transparence
                économique : permettre aux tiers de connaître les acteurs
                économiques et les responsabilités qu'ils engagent. Cette
                finalité est encadrée et limitée.
              </p>
              <p>
                Les rediffusions privées, l'indexation par les moteurs et
                l'agrégation par des plateformes commerciales prolongent
                ensuite cette publication initiale, parfois bien au-delà de
                sa finalité d'origine. La question juridique ne porte donc
                pas uniquement sur la publication initiale mais également
                sur les usages qui peuvent être faits de ces informations.
              </p>
            </div>
          </div>

          <FlowDiagram />
        </div>
      </section>

      {/* Section 06 — La publicité de la source */}
      <section
        data-testid="cadre-section-publicite-source"
        className="bg-[#FAF9F7] border-b border-[#E2DEDA]"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 mb-14">
            <div className="lg:col-span-5">
              <SectionEyebrow n={6} label="Rediffusion" />
              <h2 className="font-serif font-normal text-[#181A1D] text-3xl lg:text-4xl xl:text-5xl tracking-tight leading-[1.06] text-balance">
                La publicité de la source ne justifie pas toutes les formes de rediffusion.
              </h2>
              <div className="h-[2px] w-10 bg-[#E94B3C] mt-10" />
            </div>
            <div className="lg:col-span-7 space-y-6 text-[15px] lg:text-base text-[#52565C] font-normal leading-relaxed">
              <p>
                Une information peut avoir été légalement publiée à
                l'origine. Sa nouvelle diffusion par un acteur privé
                constitue toutefois un traitement distinct, qui doit
                avoir sa propre finalité et respecter les règles
                applicables. Le caractère public d'une source ne rend
                donc pas automatiquement toute republication nécessaire
                ou proportionnée.
              </p>
              <p>
                L'indexation nominative, l'agrégation, la cartographie,
                la mise à disposition de documents téléchargeables et la
                facilité de croisement peuvent changer concrètement la
                portée d'une information. Le passage d'une donnée
                dispersée à une fiche centralisée, nominative et
                immédiatement accessible constitue un changement
                d'échelle de l'exposition.
              </p>
              <p>
                Ces éléments ne constituent pas des conclusions
                judiciaires certaines : ils relèvent, pour partie, d'une
                analyse juridique et d'une mise en balance propres à
                chaque situation. C'est pourquoi il importe de distinguer
                soigneusement les plans suivants.
              </p>
            </div>
          </div>
          <CardGrid items={DISTINCTIONS} cols={3} testid="cadre-distinctions-grid" />
        </div>
      </section>

      {/* Section 07 — Cas par cas + Note finale */}
      <section data-testid="cadre-section-06" className="bg-[#F2F0ED]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <SectionEyebrow n={7} label="Approche" />
            <h2 className="font-serif font-normal text-[#181A1D] text-3xl lg:text-4xl xl:text-5xl tracking-tight leading-[1.06] text-balance">
              Un cadre qui s'apprécie au cas par cas.
            </h2>
            <div className="h-[2px] w-10 bg-[#E94B3C] mt-10" />
          </div>
          <div className="lg:col-span-7 space-y-6 text-[15px] lg:text-base text-[#52565C] font-normal leading-relaxed">
            <p>
              Chaque situation possède ses propres particularités : nature
              des données, source d'origine, obligations légales
              applicables, durée de diffusion, finalité poursuivie,
              conséquences concrètes pour la personne concernée.
            </p>
            <p>
              HorsIndex privilégie donc une approche documentée consistant
              à qualifier la situation avant d'envisager les démarches les
              plus adaptées.
            </p>

            {/* Note finale */}
            <aside
              data-testid="cadre-note-finale"
              className="mt-10 border border-[#E2DEDA] bg-white rounded-md p-7 lg:p-8"
            >
              <p className="label-eyebrow text-[#666A70] mb-3">
                Note importante
              </p>
              <p className="text-[13px] lg:text-sm text-[#5A5E64] italic font-normal leading-relaxed text-pretty">
                Les informations présentées sur cette page constituent des
                repères généraux. Elles ne remplacent pas une consultation
                juridique individualisée. L'appréciation d'une situation
                dépend toujours de ses circonstances particulières.
              </p>
            </aside>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
